import { ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import useCourseDetail from '../../../../hooks/use-course-detail';
import CourseToc from '../../components/course-toc/course-toc';
import LessonPlayer from '../../components/lesson-player/lesson-player';

export interface CourseDetailPageProps {
  courseId: string;
}

const CourseDetailPage = ({ courseId }: CourseDetailPageProps) => {
  const {
    course,
    isLoadingCourse,
    currentLessonId,
    currentEntry,
    currentContent,
    hasContentError,
    retryContent,
    direction,
    isCompleting,
    isLastLesson,
    goToLesson,
    completeCurrent,
  } = useCourseDetail(courseId);

  const handleComplete = async () => {
    const result = await completeCurrent();
    if (result?.isCourseCompleted) {
      toast.success('Kurs abgeschlossen! 🎉');
    }
  };

  const backLink = (
    <Link to="/learning-suite" className="inline-flex items-center gap-1.5 text-[13px] text-[#888] hover:text-[#12282A] transition-colors">
      <ArrowLeft className="w-4 h-4" />
      zur Übersicht
    </Link>
  );

  if (isLoadingCourse) {
    return (
      <div className="flex flex-col gap-4 h-full min-h-0">
        {backLink}
        <div className="h-8 w-64 bg-[#E4E7EC] rounded-full animate-pulse" />
        <div className="flex-1 min-h-0 grid grid-cols-1 grid-rows-1 lg:grid-cols-[1fr_340px] gap-4">
          <div className="bg-white rounded-2xl border border-[#E4E7EC] animate-pulse" />
          <div className="bg-white rounded-2xl border border-[#E4E7EC] animate-pulse hidden lg:block" />
        </div>
      </div>
    );
  }

  if (!course) {
    return (
      <div className="flex flex-col gap-4">
        {backLink}
        <div className="bg-white rounded-2xl border border-[#E4E7EC] p-10 text-center">
          <p className="text-[16px] text-[#12282A] font-bold mb-1">Kurs nicht gefunden</p>
          <p className="text-[14px] text-[#888]">Der Kurs ist nicht verfügbar oder wurde entfernt.</p>
        </div>
      </div>
    );
  }

  const isEmpty = !currentLessonId || !currentEntry;

  return (
    <div className="flex flex-col gap-4 h-full min-h-0">
      <div className="flex flex-col gap-1 shrink-0">
        {backLink}
        <h1 className="text-[28px] leading-[34px] font-bold text-[#12282A]">{course.title}</h1>
      </div>

      {isEmpty ? (
        <div className="bg-white rounded-2xl border border-[#E4E7EC] p-10 text-center">
          <p className="text-[16px] text-[#12282A] font-bold mb-1">Dieser Kurs hat noch keine Lektionen</p>
          <p className="text-[14px] text-[#888]">Sobald Inhalte hinzugefügt werden, kannst du hier starten.</p>
        </div>
      ) : (
        // grid-rows-1 makes the single row 1fr so it fills the bounded height. Without it the row is
        // auto-sized, and the player's absolutely-positioned lessons give it zero intrinsic height.
        <div className="flex-1 min-h-0 grid grid-cols-1 grid-rows-1 lg:grid-cols-[1fr_340px] gap-4">
          <LessonPlayer
            entry={currentEntry}
            content={currentContent}
            hasError={hasContentError}
            onRetry={retryContent}
            direction={direction}
            isLastLesson={isLastLesson}
            isCompleting={isCompleting}
            onComplete={handleComplete}
          />

          {/* Hidden on small screens — the player takes the full width there. */}
          <div className="hidden lg:block min-h-0">
            <CourseToc
              title={course.title}
              imageUrl={course.imageUrl}
              percentage={course.percentage}
              modules={course.modules}
              currentLessonId={currentLessonId}
              onSelectLesson={goToLesson}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default CourseDetailPage;
